import type { SearchCondition } from '../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import type { WidgetInstance } from './dashboard.service';
import type { MxFilterOperatorsFactory } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/column-filters/mx-grid-column-filter-operators-factory';

export interface DependentFilterItem {
	Selected: boolean;
	Value: string;
	Title: string;
	Id: string;
	Name: string;
}

export interface DependentFilterParams {
	ColumnName: string;
	WidgetInstance: string;
}

interface Dependency {
	column: string;
	widgetInstance: WidgetInstance;
}

export type DependentFilterValue = string[];

export class DependentFilter {
	dependencies: Dependency[];

	constructor(
		private readonly filterOperatorsFactory: MxFilterOperatorsFactory,
		private readonly influenceWidgetInstance: WidgetInstance,
		value?: DependentFilterValue
	) {
		this.initializeItems(value);
		this.dependencies = this.getDependencies();
	}

	private getDependencies(): Dependency[] {
		const dependencies: Dependency[] = [];
		this.influenceWidgetInstance.dashboard.widgetInstances.forEach((widgetInstance) => {
			const filterParams =
				!widgetInstance.hidden &&
				widgetInstance.widget?.DatasetRelated &&
				widgetInstance.dataModel.WidgetConfiguration.Filters?.find((filter: DependentFilterParams) => filter.WidgetInstance === this.influenceWidgetInstance.id);

			if (filterParams) {
				dependencies.push({
					column: filterParams.ColumnName,
					widgetInstance,
				});
			}
		});
		return dependencies;
	}

	get id() {
		return this.influenceWidgetInstance.id;
	}

	private initializeItems(value?: DependentFilterValue) {
		const items = this.influenceWidgetInstance.dataModel.WidgetConfiguration.items || [];

		this.influenceWidgetInstance.dataModel.Context.selectedItems = items.map((item) => ({
			...item,
			Selected: value?.includes(item.Value) ?? true,
		}));
	}

	private getItems() {
		return this.influenceWidgetInstance.dataModel.Context.selectedItems || [];
	}

	public getValue(): string[] {
		return this.getItems()
			.filter((item) => item.Selected)
			.map((item) => item.Value);
	}

	private isAllItemsSelected() {
		const items = this.getItems();
		const selectedItems = this.getValue();
		return items.length > 0 && selectedItems.length === items.length;
	}

	private isAllItemsUnSelected() {
		const items = this.getItems();
		const selectedItems = this.getValue();
		return items.length > 0 && selectedItems.length === 0;
	}

	public hasValue() {
		return this.getItems().length && !this.isAllItemsSelected();
	}

	public getSearchConditions(widgetInstance: WidgetInstance): SearchCondition[] {
		const dependency = this.dependencies.find((dependency) => dependency.widgetInstance === widgetInstance);
		if (!dependency) {
			return [];
		}

		if (!this.hasValue()) {
			return [];
		} else if (this.isAllItemsUnSelected()) {
			return [
				{
					Property: dependency.column,
					Operator: this.filterOperatorsFactory.operators.IsNotSet.value,
					Value: [],
				},
			];
		} else {
			return (
				this.getValue().map((value) => ({
					Property: dependency.column,
					Operator: this.filterOperatorsFactory.operators.Equals.value,
					Value: [value],
				})) ?? []
			);
		}
	}
}
